/**
 * React Query hooks for Persistence drift, baselines and approved writers.
 * Endpoints under /api/v1/connectors/edr/persistence/*.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { edrDelete, edrGet, edrPost, edrPut } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  ApprovedWriter,
  ApprovedWritersResponse,
  PersistenceBaseline,
  PersistenceBaselineListResponse,
  PersistenceCheckType,
  PersistenceItem,
  PersistenceSnapshot,
  PersistenceSnapshotListResponse,
} from "@/types/edrHunts";

const BASE = "/api/v1/connectors/edr/persistence";

function asArray<T>(r: { snapshots?: T[]; baselines?: T[]; data?: T[] } | null | undefined): T[] {
  if (!r) return [];
  return r.snapshots ?? r.baselines ?? r.data ?? [];
}

// ---------- Snapshots ----------

export function usePersistenceSnapshots(params?: {
  deviceId?: string;
  checkType?: string;
}) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "persistence", "snapshots", params],
    staleTime: 30_000,
    queryFn: async () => {
      const res = await edrGet<PersistenceSnapshotListResponse>(`${BASE}/snapshots`, {
        params: {
          deviceId: params?.deviceId,
          checkType: params?.checkType,
        },
      });
      return asArray<PersistenceSnapshot>(res);
    },
    retry: false,
  });
}

// ---------- Baselines ----------

export function usePersistenceBaselines() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "persistence", "baselines"],
    staleTime: 60_000,
    queryFn: async () => {
      const res = await edrGet<PersistenceBaselineListResponse>(`${BASE}/baselines`);
      return asArray<PersistenceBaseline>(res);
    },
    retry: false,
  });
}

export function useReplaceBaseline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      checkType: PersistenceCheckType;
      entries: PersistenceItem[];
    }) => {
      return edrPut<PersistenceBaseline>(`${BASE}/baselines/${input.checkType}`, {
        entries: input.entries,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["edr", "persistence", "baselines"] });
      qc.invalidateQueries({ queryKey: ["edr", "persistence", "snapshots"] });
    },
  });
}

// ---------- Approved writers ----------

export function useApprovedWriters() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "persistence", "approved-writers"],
    staleTime: 60_000,
    queryFn: async () => edrGet<ApprovedWritersResponse>(`${BASE}/approved-writers`),
    retry: false,
  });
}

export function useAddApprovedWriter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (w: ApprovedWriter) => edrPost<ApprovedWriter>(`${BASE}/approved-writers`, w),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "persistence", "approved-writers"] }),
  });
}

export function useRemoveApprovedWriter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (w: Pick<ApprovedWriter, "kind" | "value">) =>
      edrDelete<void>(`${BASE}/approved-writers`, w),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edr", "persistence", "approved-writers"] }),
  });
}
